/**
 * Process Train Step-by-Step Validation Engine
 * Generates every single calculation step for each equipment type
 * with live substituted values from sample data.
 */

export interface ValidationStep {
  stepNumber: number;
  equipmentKey: string;
  equipmentName: string;
  category: string;
  stepName: string;
  formula: string;
  substitution: string;
  result: string;
  unit: string;
  explanation: string;
  status: 'pass' | 'warn' | 'fail' | 'info';
  reference?: string;
}

export interface SampleData {
  flowRate: number;
  tss: number;
  og: number;
  bod: number;
  cod: number;
  ph: number;
  temp: number;
  alkalinity: number;
  turbidity: number;
  conductivity: number;
  hardness: number;
  nh3n: number;
  tp: number;
}

const fmt = (v: any, d = 2) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);
const fmt0 = (v: any) => fmt(v, 0);
const fmt1 = (v: any) => fmt(v, 1);
const fmt2 = (v: any) => fmt(v, 2);
const fmt3 = (v: any) => fmt(v, 3);

function statusFromRange(value: number, min: number, max: number, unit = ''): 'pass' | 'warn' | 'fail' {
  if (value >= min && value <= max) return 'pass';
  if (value >= min * 0.8 && value <= max * 1.2) return 'warn';
  return 'fail';
}

function statusFromMin(value: number, min: number): 'pass' | 'warn' | 'fail' {
  if (value >= min) return 'pass';
  if (value >= min * 0.8) return 'warn';
  return 'fail';
}

function statusFromMax(value: number, max: number): 'pass' | 'warn' | 'fail' {
  if (value <= max) return 'pass';
  if (value <= max * 1.2) return 'warn';
  return 'fail';
}

// ─── DAF VALIDATION STEPS ───────────────────────────────
function generateDAFSteps(s: SampleData): ValidationStep[] {
  const steps: ValidationStep[] = [];
  const Q = s.flowRate / 24;
  const tempK = s.temp + 273.15;
  const mu = 0.001 * Math.exp(-1.5 * (s.temp - 20) / tempK);
  const rhoWater = 998 - 0.2 * (s.temp - 20);
  const Np = 0.35;
  const N_rps = 60 / 60; // default 60 rpm
  const D_imp = 0.8; // default impeller dia
  const P_floc_W = Np * rhoWater * Math.pow(N_rps, 3) * Math.pow(D_imp, 5);
  const G = P_floc_W > 0 ? Math.sqrt(P_floc_W / (mu * 12)) : 0;
  const tipSpeed = Math.PI * D_imp * N_rps;
  const coagDose = 15;
  const coagType = 'ferric';
  const baseFactor = coagType === 'ferric' ? 0.001 : coagType === 'alum' ? 0.0006 : 0.0002;
  const precipRatio = coagType === 'ferric' ? 2.7 : coagType === 'alum' ? 2.9 : 1.5;
  const phDrop = (coagDose * baseFactor) / (1 + s.alkalinity / 100);
  const effPH = Math.max(4.0, s.ph - phDrop);
  const C_sat_20 = 25;
  const C_sat_T = C_sat_20 * Math.exp(-1300 * (1/tempK - 1/293.15));
  // Henry's law requires ABSOLUTE pressure (gauge + 1.013 bar atm). Using gauge alone
  // over-estimates dissolved air by ~20% and produces falsely optimistic A/S ratios.
  // Reference: WEF MOP 8 §6.4; Metcalf & Eddy (2014) §7.2.2
  const P_sat = 5; // bar gauge (nominal default for process train validation)
  const P_abs_sat = P_sat + 1.013; // bar absolute
  const C_sat_P = C_sat_T * P_abs_sat;
  const eta_sat = 0.85;
  const C_air = C_sat_P * eta_sat;
  const Q_recycle = Q * 0.30;
  const airMass = (C_air * Q_recycle) / 1000;
  const TSSmass = (s.tss * Q) / 1000;
  const AS = TSSmass > 0 ? airMass / TSSmass : 0;
  const bubbleSize = Math.max(20, Math.min(100, 80 - 8 * (P_sat - 4)));
  // Air density also requires absolute pressure for ideal-gas law
  const rhoAir = 1.2 * P_abs_sat * (293 / tempK);
  const d_bubble_m = bubbleSize * 1e-6;
  const bubbleRise_ms = (9.81 * (rhoWater - rhoAir) * d_bubble_m * d_bubble_m) / (18 * mu);
  const bubbleRise = bubbleRise_ms * 3600;
  const surfaceArea = 6 * 3;
  const HLR = Q / surfaceArea;
  const conductivityFactor = s.conductivity > 1000 ? Math.max(0.80, 1.0 - 0.03 * Math.log10(s.conductivity / 1000)) : 1.0;
  const hardnessFactor = s.hardness < 50 ? 0.95 : s.hardness > 300 ? Math.min(1.08, 1.0 + 0.05 * ((s.hardness - 300) / 200)) : 1.0;
  // adjustedCoagBoost already incorporates chemistry factors — do NOT multiply eff_TSS
  // by them again. Doing so squares the penalty (~0.87² instead of 0.87 at cond=8000).
  const adjustedCoagBoost = 0.30 * conductivityFactor * hardnessFactor;
  const base_TSS = 0.45;
  // Outer multiply removed: conductivityFactor/hardnessFactor are already baked into adjustedCoagBoost
  const eff_TSS = Math.min(base_TSS + adjustedCoagBoost, 0.98);
  // O&G: single application — base O&G removal IS affected by water chemistry
  const eff_OG = 0.65 * conductivityFactor * hardnessFactor;
  const effluentTSS = s.tss * (1 - eff_TSS);
  const effluentOG = s.og * (1 - eff_OG);

  steps.push({
    stepNumber: 1, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Flow rate conversion', formula: 'Q = flowRate / 24',
    substitution: `Q = ${fmt0(s.flowRate)} / 24 = ${fmt1(Q)}`, result: fmt1(Q), unit: 'm³/h',
    explanation: 'Convert daily flow to hourly for DAF sizing calculations',
    status: statusFromRange(Q, 2, 100), reference: '§2.1 Hydraulic Loading'
  });

  steps.push({
    stepNumber: 2, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Water density', formula: 'ρ_w = 998 − 0.2×(T−20)',
    substitution: `ρ_w = 998 − 0.2×(${s.temp}−20) = ${fmt0(rhoWater)}`, result: fmt0(rhoWater), unit: 'kg/m³',
    explanation: 'Water density decreases with temperature, affecting buoyancy and settling',
    status: 'info', reference: '§2.1 Physical Properties'
  });

  steps.push({
    stepNumber: 3, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Dynamic viscosity', formula: 'μ = 0.001 × exp[−1.5×(T−20)/(T+273.15)]',
    substitution: `μ = 0.001 × exp[−1.5×${s.temp - 20}/${fmt1(tempK)}] = ${fmt3(mu * 1000)}`, result: fmt3(mu * 1000), unit: 'mPa·s',
    explanation: 'Viscosity controls Stokes settling and power dissipation in mixing',
    status: 'info', reference: '§2.1 Physical Properties'
  });

  steps.push({
    stepNumber: 4, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Conductivity factor', formula: 'f_cond = max(0.80, 1 − 0.03×log₁₀(cond/1000))',
    substitution: `f_cond = max(0.80, 1 − 0.03×log₁₀(${s.conductivity}/1000)) = ${fmt3(conductivityFactor)}`, result: fmt3(conductivityFactor), unit: '',
    explanation: 'High ionic strength shields colloid charges, reducing coagulant efficiency',
    status: conductivityFactor >= 0.95 ? 'pass' : conductivityFactor >= 0.90 ? 'warn' : 'fail', reference: '§2.2 Coagulation Chemistry'
  });

  steps.push({
    stepNumber: 5, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Hardness factor', formula: 'f_hard = 0.95→1.08 based on Ca²⁺/Mg²⁺',
    substitution: `Hardness = ${s.hardness} mg/L → f_hard = ${fmt3(hardnessFactor)}`, result: fmt3(hardnessFactor), unit: '',
    explanation: 'Calcium and magnesium ions aid sweep floc coagulation; too low = poor floc',
    status: hardnessFactor >= 0.98 ? 'pass' : hardnessFactor < 0.95 ? 'fail' : 'warn', reference: '§2.2 Coagulation Chemistry'
  });

  steps.push({
    stepNumber: 6, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Coagulant pH drop', formula: 'ΔpH = (dose × factor) / (1 + alkalinity/100)',
    substitution: `ΔpH = (${coagDose} × ${baseFactor}) / (1 + ${s.alkalinity}/100) = ${fmt3(phDrop)}`, result: fmt3(phDrop), unit: 'pH units',
    explanation: 'Coagulant hydrolysis produces acid; alkalinity buffers the drop',
    status: phDrop < 0.5 ? 'pass' : phDrop < 1.0 ? 'warn' : 'fail', reference: '§2.2 Buffer Capacity'
  });

  steps.push({
    stepNumber: 7, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Effluent pH', formula: 'pH_eff = pH_in − ΔpH',
    substitution: `pH_eff = ${fmt1(s.ph)} − ${fmt3(phDrop)} = ${fmt2(effPH)}`, result: fmt2(effPH), unit: '',
    explanation: 'Final pH after coagulant addition must remain > 6.0 for DAF operation',
    status: effPH > 6.0 ? 'pass' : 'fail', reference: '§2.2 pH Compliance'
  });

  steps.push({
    stepNumber: 8, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Coagulant precipitate mass', formula: 'm = dose × ratio × Q / 1000',
    substitution: `m = ${coagDose} × ${precipRatio} × ${fmt0(s.flowRate)} / 1000 = ${fmt1(coagDose * precipRatio * s.flowRate / 1000)}`, result: fmt1(coagDose * precipRatio * s.flowRate / 1000), unit: 'kg/d',
    explanation: 'Metal hydroxide precipitate adds to sludge mass',
    status: 'info', reference: '§2.2 Sludge Production'
  });

  steps.push({
    stepNumber: 9, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Air solubility (Henry + van\'t Hoff)', formula: 'C_sat = 25 × exp[−1300(1/T − 1/293.15)] × P_abs × η  [P_abs = P_gauge + 1.013]',
    substitution: `C_sat = 25 × exp[−1300(1/${fmt1(tempK)} − 1/293.15)] × (${P_sat}+1.013) × ${eta_sat} = ${fmt1(C_air)}`, result: fmt1(C_air), unit: 'mg/L',
    explanation: 'Dissolved air at absolute pressure — Henry\'s law requires P_abs = P_gauge + 1.013 bar',
    status: C_air > 50 ? 'pass' : C_air > 30 ? 'warn' : 'fail', reference: '§2.3 Henry\'s Law'
  });

  steps.push({
    stepNumber: 10, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'A/S ratio', formula: 'A/S = m_air / m_TSS',
    substitution: `A/S = ${fmt2(airMass)} / ${fmt2(TSSmass)} = ${fmt3(AS)}`, result: fmt3(AS), unit: '',
    explanation: 'Air-to-solids ratio determines flotation efficiency. Optimal: 0.03–0.08',
    status: AS >= 0.03 && AS <= 0.08 ? 'pass' : AS >= 0.02 ? 'warn' : 'fail', reference: '§2.3 Flotation Kinetics'
  });

  steps.push({
    stepNumber: 11, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Bubble diameter', formula: 'd_b = 80 − 8×(P_sat − 4)',
    substitution: `d_b = 80 − 8×(${P_sat} − 4) = ${fmt0(bubbleSize)}`, result: fmt0(bubbleSize), unit: 'μm',
    explanation: 'Higher pressure = smaller bubbles = better attachment to particles',
    status: bubbleSize >= 30 && bubbleSize <= 80 ? 'pass' : 'warn', reference: '§2.4 Bubble Physics'
  });

  steps.push({
    stepNumber: 12, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Bubble rise velocity (Stokes)', formula: 'v = g×(ρ_w−ρ_a)×d² / 18μ',
    substitution: `v = 9.81×(${fmt0(rhoWater)}−${fmt1(rhoAir)})×(${bubbleSize}×10⁻⁶)² / (18×${fmt3(mu)}) = ${fmt1(bubbleRise)}`, result: fmt1(bubbleRise), unit: 'm/h',
    explanation: 'Rise velocity must exceed downward water velocity for particle capture',
    status: bubbleRise >= 5 && bubbleRise <= 15 ? 'pass' : 'warn', reference: '§2.4 Stokes Law'
  });

  steps.push({
    stepNumber: 13, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Flocculation power (power number)', formula: 'P = Np × ρ × N³ × D⁵',
    substitution: `P = ${Np} × ${fmt0(rhoWater)} × (${fmt3(N_rps)})³ × (${fmt1(D_imp)})⁵ = ${fmt1(P_floc_W)}`, result: fmt1(P_floc_W), unit: 'W',
    explanation: 'Actual power dissipated by Rushton turbine in flocculation tank',
    status: 'info', reference: '§2.5 Camp-Stein'
  });

  steps.push({
    stepNumber: 14, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Camp-Stein G-value', formula: 'G = √(P / μV)',
    substitution: `G = √(${fmt1(P_floc_W)} / (${fmt3(mu)} × 12)) = ${fmt0(G)}`, result: fmt0(G), unit: 's⁻¹',
    explanation: 'Velocity gradient controls floc growth. Optimal: 20–100 s⁻¹',
    status: statusFromRange(G, 20, 100), reference: '§2.5 Camp-Stein'
  });

  steps.push({
    stepNumber: 15, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Impeller tip speed', formula: 'v_tip = π × D × N',
    substitution: `v_tip = π × ${fmt1(D_imp)} × ${fmt3(N_rps)} = ${fmt1(tipSpeed)}`, result: fmt1(tipSpeed), unit: 'm/s',
    explanation: 'Tip speed must stay below 5 m/s to prevent floc shear damage',
    status: tipSpeed <= 5 ? 'pass' : 'fail', reference: '§2.5 Shear Control'
  });

  steps.push({
    stepNumber: 16, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Hydraulic loading rate', formula: 'HLR = Q / A',
    substitution: `HLR = ${fmt1(Q)} / ${surfaceArea} = ${fmt1(HLR)}`, result: fmt1(HLR), unit: 'm³/m²·h',
    explanation: 'Surface loading must be 1–8 m³/m²·h for effective separation',
    status: statusFromRange(HLR, 1, 8), reference: '§2.6 Surface Loading'
  });

  steps.push({
    stepNumber: 17, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'TSS removal efficiency', formula: 'η_TSS = (base + boost) × f_cond × f_hard',
    substitution: `η = (${base_TSS} + ${fmt3(adjustedCoagBoost)}) = ${fmt2(eff_TSS * 100)}%`, result: fmt2(eff_TSS * 100), unit: '%',
    explanation: 'Overall TSS removal including coagulation boost and water chemistry effects',
    status: eff_TSS >= 0.70 ? 'pass' : eff_TSS >= 0.55 ? 'warn' : 'fail', reference: '§2.6 Removal Model'
  });

  steps.push({
    stepNumber: 18, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Effluent TSS', formula: 'TSS_eff = TSS_in × (1 − η)',
    substitution: `TSS_eff = ${fmt0(s.tss)} × (1 − ${fmt3(eff_TSS)}) = ${fmt1(effluentTSS)}`, result: fmt1(effluentTSS), unit: 'mg/L',
    explanation: 'Predicted effluent TSS concentration after DAF treatment',
    status: effluentTSS < 50 ? 'pass' : effluentTSS < 100 ? 'warn' : 'fail', reference: '§2.6 Effluent Quality'
  });

  steps.push({
    stepNumber: 19, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Effluent O&G', formula: 'O&G_eff = O&G_in × (1 − η_O&G)',
    substitution: `O&G_eff = ${fmt0(s.og)} × (1 − ${fmt3(eff_OG)}) = ${fmt1(effluentOG)}`, result: fmt1(effluentOG), unit: 'mg/L',
    explanation: 'Predicted effluent oil & grease after flotation',
    status: effluentOG < 15 ? 'pass' : effluentOG < 30 ? 'warn' : 'fail', reference: '§2.6 Effluent Quality'
  });

  steps.push({
    stepNumber: 20, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Nutrient check — NH₃-N', formula: '—',
    substitution: `NH₃-N = ${fmt0(s.nh3n)} mg/L`, result: fmt0(s.nh3n), unit: 'mg/L',
    explanation: 'High ammonia indicates need for biological post-treatment (nitrification)',
    status: s.nh3n <= 25 ? 'pass' : s.nh3n <= 100 ? 'warn' : 'fail', reference: '§2.8 Nutrients'
  });

  steps.push({
    stepNumber: 21, equipmentKey: 'daf', equipmentName: 'DAF Unit', category: 'primary',
    stepName: 'Nutrient check — Total Phosphorus', formula: '—',
    substitution: `TP = ${fmt1(s.tp)} mg/L`, result: fmt1(s.tp), unit: 'mg/L',
    explanation: 'High TP may require chemical precipitation or tertiary P-removal',
    status: s.tp <= 5 ? 'pass' : s.tp <= 20 ? 'warn' : 'fail', reference: '§2.8 Nutrients'
  });

  return steps;
}

// ─── LAMELLA VALIDATION STEPS ───────────────────────────
function generateLamellaSteps(s: SampleData): ValidationStep[] {
  const steps: ValidationStep[] = [];
  const Q = s.flowRate / 24;
  const tankArea = 6 * 3;
  const plateSpacingM = 50 / 1000;
  const plateAngleRad = (60 * Math.PI) / 180;
  const numPlates = Math.floor(2.5 / (plateSpacingM + 5 / 1000));
  const projectedArea = 1.2 * Math.cos(plateAngleRad) * 3;
  const totalSettlingArea = numPlates * projectedArea;
  const surfaceLoading = Q / totalSettlingArea;
  const tempK = s.temp + 273.15;
  const mu = 0.001 * Math.exp(-1.5 * (s.temp - 20) / tempK);
  const rhoWater = 998 - 0.2 * (s.temp - 20);
  const particleDia = 50e-6;
  const rhoParticle = 2000;
  const settlingVelocity = (9.81 * (rhoParticle - rhoWater) * particleDia * particleDia) / (18 * mu) * 3600;
  const concFactor = Math.max(0.5, 1 - (s.tss / 2000));
  const hinderedSettling = settlingVelocity * concFactor;
  const hydraulicRadius = plateSpacingM / 2;
  const flowVelocity = Q / (numPlates * plateSpacingM * 3);
  const reynoldsNumber = (rhoWater * (flowVelocity / 3600) * hydraulicRadius) / mu;
  const flowRegime = reynoldsNumber < 500 ? 'laminar' : reynoldsNumber < 2000 ? 'transitional' : 'turbulent';
  const v0 = flowVelocity;
  const vs = hinderedSettling;
  const removalFactor = v0 > 0 ? (vs / v0) * (1.2 / plateSpacingM) * Math.sin(plateAngleRad) * Math.cos(plateAngleRad) : 0;
  const tssRemoval = Math.min(0.95, removalFactor * 0.8);
  const campEfficiency = vs > 0 && v0 > 0 ? vs / (vs + v0) : 0;

  steps.push({ stepNumber: 1, equipmentKey: 'lamella', equipmentName: 'Lamella Clarifier', category: 'primary', stepName: 'Hourly flow rate', formula: 'Q = flowRate / 24', substitution: `Q = ${fmt0(s.flowRate)} / 24 = ${fmt1(Q)}`, result: fmt1(Q), unit: 'm³/h', explanation: 'Convert daily flow to hourly basis', status: 'info', reference: '§3.1 Hydraulics' });
  steps.push({ stepNumber: 2, equipmentKey: 'lamella', equipmentName: 'Lamella Clarifier', category: 'primary', stepName: 'Number of plates', formula: 'N = floor(depth / (s + t))', substitution: `N = floor(2.5 / (${fmt3(plateSpacingM)} + 0.005)) = ${numPlates}`, result: String(numPlates), unit: 'plates', explanation: 'Count of inclined plates providing projected settling area', status: numPlates >= 5 ? 'pass' : 'fail', reference: '§3.2 Plate Geometry' });
  steps.push({ stepNumber: 3, equipmentKey: 'lamella', equipmentName: 'Lamella Clarifier', category: 'primary', stepName: 'Total settling area', formula: 'A = N × L × W × cos(θ)', substitution: `A = ${numPlates} × 1.2 × 3 × cos(60°) = ${fmt1(totalSettlingArea)}`, result: fmt1(totalSettlingArea), unit: 'm²', explanation: 'Projected horizontal area available for particle settling', status: 'info', reference: '§3.2 Plate Geometry' });
  steps.push({ stepNumber: 4, equipmentKey: 'lamella', equipmentName: 'Lamella Clarifier', category: 'primary', stepName: 'Water viscosity', formula: 'μ = 0.001 × exp[−1.5×(T−20)/(T+273.15)]', substitution: `μ = 0.001 × exp[−1.5×${s.temp - 20}/${fmt1(tempK)}] = ${fmt3(mu * 1000)}`, result: fmt3(mu * 1000), unit: 'mPa·s', explanation: 'Dynamic viscosity for Stokes and Reynolds calculations', status: 'info', reference: '§3.1 Physical Properties' });
  steps.push({ stepNumber: 5, equipmentKey: 'lamella', equipmentName: 'Lamella Clarifier', category: 'primary', stepName: 'Stokes settling velocity', formula: 'v_s = g×(ρ_p−ρ_w)×d² / 18μ', substitution: `v_s = 9.81×(2000−${fmt0(rhoWater)})×(50×10⁻⁶)² / (18×${fmt3(mu)}) = ${fmt2(settlingVelocity)}`, result: fmt2(settlingVelocity), unit: 'm/h', explanation: 'Theoretical settling velocity of 50 μm particles in quiescent water', status: 'info', reference: '§3.3 Stokes Law' });
  steps.push({ stepNumber: 6, equipmentKey: 'lamella', equipmentName: 'Lamella Clarifier', category: 'primary', stepName: 'Hindered settling factor', formula: 'f = max(0.5, 1 − TSS/2000)', substitution: `f = max(0.5, 1 − ${s.tss}/2000) = ${fmt2(concFactor)}`, result: fmt2(concFactor), unit: '', explanation: 'High solids concentration reduces effective settling velocity', status: 'info', reference: '§3.3 Hindered Settling' });
  steps.push({ stepNumber: 7, equipmentKey: 'lamella', equipmentName: 'Lamella Clarifier', category: 'primary', stepName: 'Reynolds number (plate gap)', formula: 'Re = ρ×v×R / μ', substitution: `Re = ${fmt0(rhoWater)}×${fmt3(flowVelocity / 3600)}×${fmt3(hydraulicRadius)} / ${fmt3(mu)} = ${fmt0(reynoldsNumber)}`, result: fmt0(reynoldsNumber), unit: '', explanation: 'Flow regime in plate gap. Must be < 2000 to avoid turbulent re-suspension', status: reynoldsNumber < 2000 ? 'pass' : 'fail', reference: '§3.4 Flow Regime' });
  steps.push({ stepNumber: 8, equipmentKey: 'lamella', equipmentName: 'Lamella Clarifier', category: 'primary', stepName: 'Flow regime', formula: 'Re < 500 = laminar, < 2000 = transitional', substitution: `Re = ${fmt0(reynoldsNumber)} → ${flowRegime}`, result: flowRegime, unit: '', explanation: 'Laminar flow is essential for quiescent settling conditions', status: flowRegime === 'laminar' ? 'pass' : flowRegime === 'transitional' ? 'warn' : 'fail', reference: '§3.4 Flow Regime' });
  steps.push({ stepNumber: 9, equipmentKey: 'lamella', equipmentName: 'Lamella Clarifier', category: 'primary', stepName: 'Surface loading rate', formula: 'HLR = Q / A_total', substitution: `HLR = ${fmt1(Q)} / ${fmt1(totalSettlingArea)} = ${fmt2(surfaceLoading)}`, result: fmt2(surfaceLoading), unit: 'm³/m²·h', explanation: 'Critical design parameter. Must be < 2.5 m³/m²·h for acceptable performance', status: surfaceLoading <= 1.5 ? 'pass' : surfaceLoading <= 2.5 ? 'warn' : 'fail', reference: '§3.5 Surface Loading' });
  steps.push({ stepNumber: 10, equipmentKey: 'lamella', equipmentName: 'Lamella Clarifier', category: 'primary', stepName: 'TSS removal (Camp model)', formula: 'η = (v_s/v_0)(L/s)sinθcosθ × 0.8', substitution: `η = (${fmt2(vs)}/${fmt2(v0)}) × (1.2/${fmt3(plateSpacingM)}) × sin(60°)cos(60°) × 0.8 = ${fmt1(tssRemoval * 100)}%`, result: fmt1(tssRemoval * 100), unit: '%', explanation: 'Camp model for inclined plate settling efficiency', status: tssRemoval >= 0.70 ? 'pass' : tssRemoval >= 0.55 ? 'warn' : 'fail', reference: '§3.6 Camp Efficiency' });
  steps.push({ stepNumber: 11, equipmentKey: 'lamella', equipmentName: 'Lamella Clarifier', category: 'primary', stepName: 'Camp efficiency', formula: 'ε = v_s / (v_s + v_0)', substitution: `ε = ${fmt2(vs)} / (${fmt2(vs)} + ${fmt2(v0)}) = ${fmt2(campEfficiency)}`, result: fmt2(campEfficiency), unit: '', explanation: 'Ratio of settling velocity to total velocity. > 0.5 indicates good capture', status: campEfficiency > 0.5 ? 'pass' : 'warn', reference: '§3.6 Camp Efficiency' });
  steps.push({ stepNumber: 12, equipmentKey: 'lamella', equipmentName: 'Lamella Clarifier', category: 'primary', stepName: 'Sludge production', formula: 'm = flowRate × TSS × η / 1000', substitution: `m = ${fmt0(s.flowRate)} × ${s.tss} × ${fmt2(tssRemoval)} / 1000 = ${fmt1(s.flowRate * s.tss * tssRemoval / 1000)}`, result: fmt1(s.flowRate * s.tss * tssRemoval / 1000), unit: 'kg/d', explanation: 'Mass of TSS removed and collected as sludge', status: 'info', reference: '§3.7 Sludge' });

  return steps;
}

// ─── GENERIC STEPS FOR OTHER EQUIPMENT ──────────────────
function generateGenericSteps(key: string, name: string, category: string, s: SampleData): ValidationStep[] {
  const Q = s.flowRate / 24;
  const tempK = s.temp + 273.15;
  const mu = 0.001 * Math.exp(-1.5 * (s.temp - 20) / tempK);
  const rhoWater = 998 - 0.2 * (s.temp - 20);

  return [
    { stepNumber: 1, equipmentKey: key, equipmentName: name, category,
      stepName: 'Hourly flow rate', formula: 'Q = flowRate / 24',
      substitution: `Q = ${fmt0(s.flowRate)} / 24 = ${fmt1(Q)}`, result: fmt1(Q), unit: 'm³/h',
      explanation: 'Convert daily flow to hourly basis for equipment sizing',
      status: 'info', reference: '§Hydraulic Basis' },
    { stepNumber: 2, equipmentKey: key, equipmentName: name, category,
      stepName: 'Water density', formula: 'ρ_w = 998 − 0.2×(T−20)',
      substitution: `ρ_w = 998 − 0.2×(${s.temp}−20) = ${fmt0(rhoWater)}`, result: fmt0(rhoWater), unit: 'kg/m³',
      explanation: 'Density affects buoyancy, pressure, and power calculations',
      status: 'info', reference: '§Physical Properties' },
    { stepNumber: 3, equipmentKey: key, equipmentName: name, category,
      stepName: 'Dynamic viscosity', formula: 'μ = 0.001 × exp[−1.5×(T−20)/(T+273.15)]',
      substitution: `μ = 0.001 × exp[−1.5×${s.temp - 20}/${fmt1(tempK)}] = ${fmt3(mu * 1000)}`, result: fmt3(mu * 1000), unit: 'mPa·s',
      explanation: 'Viscosity controls fluid friction, settling, and mixing',
      status: 'info', reference: '§Physical Properties' },
    { stepNumber: 4, equipmentKey: key, equipmentName: name, category,
      stepName: 'Influent TSS loading', formula: 'L = TSS × Q / 1000',
      substitution: `L = ${fmt0(s.tss)} × ${fmt1(Q)} / 1000 = ${fmt2(s.tss * Q / 1000)}`, result: fmt2(s.tss * Q / 1000), unit: 'kg/h',
      explanation: 'Solids mass loading rate into the equipment',
      status: 'info', reference: '§Mass Balance' },
    { stepNumber: 5, equipmentKey: key, equipmentName: name, category,
      stepName: 'Influent COD loading', formula: 'L = COD × Q / 1000',
      substitution: `L = ${fmt0(s.cod)} × ${fmt1(Q)} / 1000 = ${fmt2(s.cod * Q / 1000)}`, result: fmt2(s.cod * Q / 1000), unit: 'kg/h',
      explanation: 'Organic mass loading rate into the equipment',
      status: 'info', reference: '§Mass Balance' },
    { stepNumber: 6, equipmentKey: key, equipmentName: name, category,
      stepName: 'Influent BOD loading', formula: 'L = BOD × Q / 1000',
      substitution: `L = ${fmt0(s.bod)} × ${fmt1(Q)} / 1000 = ${fmt2(s.bod * Q / 1000)}`, result: fmt2(s.bod * Q / 1000), unit: 'kg/h',
      explanation: 'Biodegradable organic mass loading rate',
      status: 'info', reference: '§Mass Balance' },
    { stepNumber: 7, equipmentKey: key, equipmentName: name, category,
      stepName: 'pH assessment', formula: '—',
      substitution: `pH = ${fmt1(s.ph)}`, result: fmt1(s.ph), unit: '',
      explanation: 'pH must be 6.5–8.5 for biological processes, 4.5–9.5 for DAF',
      status: s.ph >= 6.0 && s.ph <= 9.0 ? 'pass' : s.ph >= 5.0 && s.ph <= 10.0 ? 'warn' : 'fail', reference: '§Process Limits' },
    { stepNumber: 8, equipmentKey: key, equipmentName: name, category,
      stepName: 'Temperature assessment', formula: '—',
      substitution: `T = ${fmt0(s.temp)}°C`, result: fmt0(s.temp), unit: '°C',
      explanation: 'Optimal range 15–25°C for biological, 5–35°C for DAF',
      status: s.temp >= 10 && s.temp <= 30 ? 'pass' : s.temp >= 5 && s.temp <= 35 ? 'warn' : 'fail', reference: '§Process Limits' },
    { stepNumber: 9, equipmentKey: key, equipmentName: name, category,
      stepName: 'Conductivity assessment', formula: '—',
      substitution: `Conductivity = ${fmt0(s.conductivity)} μS/cm`, result: fmt0(s.conductivity), unit: 'μS/cm',
      explanation: '>8000 μS/cm reduces coagulant efficiency due to ionic shielding',
      status: s.conductivity <= 3000 ? 'pass' : s.conductivity <= 8000 ? 'warn' : 'fail', reference: '§Water Chemistry' },
    { stepNumber: 10, equipmentKey: key, equipmentName: name, category,
      stepName: 'Hardness assessment', formula: '—',
      substitution: `Hardness = ${fmt0(s.hardness)} mg/L CaCO₃`, result: fmt0(s.hardness), unit: 'mg/L',
      explanation: '>500 mg/L indicates scaling risk in pipework and saturators',
      status: s.hardness <= 300 ? 'pass' : s.hardness <= 500 ? 'warn' : 'fail', reference: '§Water Chemistry' },
    { stepNumber: 11, equipmentKey: key, equipmentName: name, category,
      stepName: 'NH₃-N assessment', formula: '—',
      substitution: `NH₃-N = ${fmt0(s.nh3n)} mg/L`, result: fmt0(s.nh3n), unit: 'mg/L',
      explanation: '>100 mg/L requires biological nitrification post-treatment',
      status: s.nh3n <= 25 ? 'pass' : s.nh3n <= 100 ? 'warn' : 'fail', reference: '§Nutrients' },
    { stepNumber: 12, equipmentKey: key, equipmentName: name, category,
      stepName: 'Total Phosphorus assessment', formula: '—',
      substitution: `TP = ${fmt1(s.tp)} mg/L`, result: fmt1(s.tp), unit: 'mg/L',
      explanation: '>20 mg/L may require tertiary P-removal or enhanced coagulation',
      status: s.tp <= 5 ? 'pass' : s.tp <= 20 ? 'warn' : 'fail', reference: '§Nutrients' },
  ];
}

// ─── MASTER GENERATOR ───────────────────────────────────
export function generateProcessTrainValidation(
  equipmentKeys: string[],
  sample: Partial<Record<string, number>>
): ValidationStep[] {
  const s: SampleData = {
    flowRate: Number(sample.flowRate ?? sample.flow_rate ?? 250),
    tss: Number(sample.tss ?? 250),
    og: Number(sample.oilGrease ?? sample.oil_grease ?? sample.og ?? 80),
    bod: Number(sample.bod5 ?? sample.bod ?? 1500),
    cod: Number(sample.cod ?? 3000),
    ph: Number(sample.ph ?? 7.0),
    temp: Number(sample.temperature ?? 20),
    alkalinity: Number(sample.alkalinity ?? sample.alk ?? 100),
    turbidity: Number(sample.turbidity ?? sample.turb ?? sample.ntu ?? 200),
    conductivity: Number(sample.conductivity ?? sample.cond ?? sample.ec ?? 1500),
    hardness: Number(sample.hardness ?? sample.hard ?? sample.total_hardness ?? 200),
    nh3n: Number(sample.nh3n ?? sample.nh3_n ?? sample.ammonia ?? sample.nh3 ?? 25),
    tp: Number(sample.tp ?? sample.totalPhosphorus ?? sample.total_phosphorus ?? sample.phosphorus ?? 5),
  };

  const allSteps: ValidationStep[] = [];

  for (const key of equipmentKeys) {
    let steps: ValidationStep[];
    switch (key) {
      case 'daf': steps = generateDAFSteps(s); break;
      case 'lamella': steps = generateLamellaSteps(s); break;
      case 'eq': steps = generateGenericSteps('eq', 'Equalization Tank', 'pretreatment', s); break;
      case 'buffer': steps = generateGenericSteps('buffer', 'Buffer Tank', 'pretreatment', s); break;
      case 'gac': steps = generateGenericSteps('gac', 'GAC Filter', 'tertiary', s); break;
      case 'multimedia': steps = generateGenericSteps('multimedia', 'Multimedia Filter', 'primary', s); break;
      case 'aeration': steps = generateGenericSteps('aeration', 'Aeration Tank', 'secondary', s); break;
      case 'secondary': steps = generateGenericSteps('secondary', 'Secondary Clarifier', 'secondary', s); break;
      case 'ph': steps = generateGenericSteps('ph', 'pH Adjustment', 'tertiary', s); break;
      case 'sludge': steps = generateGenericSteps('sludge', 'Sludge Holding', 'sludge', s); break;
      default: steps = generateGenericSteps(key, key, 'primary', s); break;
    }
    allSteps.push(...steps);
  }

  return allSteps;
}
